/**
 * Web Push no navegador (0.1.49) — ativar/desativar notificações do PWA.
 *
 * Fluxo do "Ativar notificações": registra o service worker, pede permissão,
 * pega a chave pública VAPID do servidor, cria a inscrição e a envia ao backend.
 * Tudo degrada com elegância se o navegador não suportar (ex.: iOS sem "adicionar
 * à tela de início").
 */
import { api } from "./api";

export function suportaPush(): boolean {
  return (
    typeof window !== "undefined" &&
    "serviceWorker" in navigator &&
    "PushManager" in window &&
    "Notification" in window
  );
}

function base64ToUint8(base64: string): Uint8Array {
  const pad = "=".repeat((4 - (base64.length % 4)) % 4);
  const b64 = (base64 + pad).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(b64);
  const out = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) out[i] = raw.charCodeAt(i);
  return out;
}

async function registrarSW(): Promise<ServiceWorkerRegistration> {
  return navigator.serviceWorker.register("/sw.js");
}

/** Já estou inscrito NESTE aparelho? */
export async function estaAtivo(): Promise<boolean> {
  if (!suportaPush() || Notification.permission !== "granted") return false;
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    return !!sub;
  } catch {
    return false;
  }
}

/** Re-envia ao servidor a inscrição ATUAL deste aparelho (auto-cura endpoint trocado).
 *  Silencioso: não faz nada se não houver permissão/inscrição. */
export async function sincronizarInscricao(condominioId: number): Promise<void> {
  try {
    if (!suportaPush() || Notification.permission !== "granted") return;
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (!sub) return;
    const j = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
    await api.post(`/condominios/${condominioId}/push/inscrever`, {
      endpoint: j.endpoint, p256dh: j.keys?.p256dh, auth: j.keys?.auth,
    });
  } catch {
    /* silencioso */
  }
}

/** Ativa as notificações para este condomínio. Devolve true se deu certo. */
export async function ativarNotificacoes(condominioId: number): Promise<boolean> {
  if (!suportaPush()) throw new Error("nao-suportado");
  const perm = await Notification.requestPermission();
  if (perm !== "granted") throw new Error("permissao-negada");

  const reg = await registrarSW();
  await navigator.serviceWorker.ready;

  const { chave } = await api.get<{ chave: string }>("/push/chave-publica");
  if (!chave) throw new Error("sem-chave-no-servidor");
  const appKey = base64ToUint8(chave) as BufferSource;
  // Reaproveita a inscrição existente (comportamento estável). Só cria uma nova
  // quando não há nenhuma — evita o erro do Chrome "could not retrieve the public
  // key" que aparece ao re-inscrever logo após um unsubscribe.
  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    try {
      sub = await reg.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: appKey,
      });
    } catch (e) {
      const nome = (e as Error).name || "";
      const msg = (e as Error).message || String(e);
      throw new Error(`falha-inscricao:${nome}:${msg}`);
    }
  }
  const json = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  await api.post(`/condominios/${condominioId}/push/inscrever`, {
    endpoint: json.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
  });
  return true;
}

/** Envia uma notificação de teste para este aparelho (diagnóstico). */
export async function enviarTeste(): Promise<{ ok: boolean; sem_lib: boolean; detalhe: string }> {
  const reg = await navigator.serviceWorker.getRegistration();
  const sub = await reg?.pushManager.getSubscription();
  if (!sub) throw new Error("sem-inscricao");
  const j = sub.toJSON() as { endpoint?: string; keys?: { p256dh?: string; auth?: string } };
  return api.post("/push/testar", {
    endpoint: j.endpoint, p256dh: j.keys?.p256dh, auth: j.keys?.auth,
  });
}

/** Desativa neste aparelho. */
export async function desativarNotificacoes(): Promise<void> {
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = await reg?.pushManager.getSubscription();
    if (sub) {
      await api.post("/push/desinscrever", { endpoint: sub.endpoint }).catch(() => {});
      await sub.unsubscribe().catch(() => {});
    }
  } catch {
    /* silencioso */
  }
}
